export interface Theme {
  name: string;
  displayName: string;
  colors: {
    // Main colors
    primary: string;
    primaryLight: string;
    primaryDark: string;
    secondary: string;

    // Background colors
    background: string;
    backgroundGradientStart: string;
    backgroundGradientEnd: string;
    headerBackground: string;

    // Text colors
    textPrimary: string;
    textSecondary: string;
    textHeading: string;

    // Component colors
    cardBackground: string;
    cardBorder: string;
    navBackground: string;
    navBorder: string;
    navActiveBackground: string;

    // Status colors
    success: string;
    warning: string;
    info: string;

    // Interactive
    linkColor: string;
    linkHover: string;
    buttonBackground: string;
    buttonHover: string;

    // Effects
    shadowColor: string;
    overlayBackground: string;
  };
  typography: {
    // Font families
    fontFamily: string;
    fontFamilyHeading: string;
    fontFamilyMono: string;

    // Font sizes
    fontSizeBase: string;
    fontSizeSmall: string;
    fontSizeLarge: string;
    fontSizeH1: string;
    fontSizeH2: string;
    fontSizeH3: string;

    // Font weights
    fontWeightNormal: string;
    fontWeightMedium: string;
    fontWeightBold: string;

    // Line heights
    lineHeightBase: string;
    lineHeightHeading: string;

    // Letter spacing
    letterSpacingNormal: string;
    letterSpacingWide: string;
  };
}

const defaultTypography = {
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
  fontFamilyHeading:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
  fontFamilyMono:
    'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
  fontSizeBase: '1.15rem',
  fontSizeSmall: '0.95rem',
  fontSizeLarge: '1.35rem',
  fontSizeH1: '2.5rem',
  fontSizeH2: '2rem',
  fontSizeH3: '1.75rem',
  fontWeightNormal: '400',
  fontWeightMedium: '500',
  fontWeightBold: '700',
  lineHeightBase: '1.6',
  lineHeightHeading: '1.3',
  letterSpacingNormal: 'normal',
  letterSpacingWide: '0.05em',
};

export const themes: Record<string, Theme> = {
  ukrainian: {
    name: 'ukrainian',
    displayName: 'За замовчуванням',
    colors: {
      primary: '#4a7ba7',
      primaryLight: '#6a95bb',
      primaryDark: '#355c85',
      secondary: '#d4af37',

      background: '#252a35',
      backgroundGradientStart: '#2a3340',
      backgroundGradientEnd: '#252a35',
      headerBackground: 'linear-gradient(135deg, #3d5a75 0%, #4a6075 100%)',

      textPrimary: '#e8e8e8',
      textSecondary: '#d0d0d0',
      textHeading: '#e0c080',

      cardBackground: 'rgba(74, 123, 167, 0.12)',
      cardBorder: 'rgba(212, 175, 55, 0.2)',
      navBackground: 'rgba(74, 123, 167, 0.15)',
      navBorder: 'rgba(212, 175, 55, 0.25)',
      navActiveBackground: 'linear-gradient(135deg, #556a7d 0%, #4d5f72 100%)',

      success: '#4caf50',
      warning: '#ff9800',
      info: '#6a95bb',

      linkColor: '#c9a860',
      linkHover: '#d4af37',
      buttonBackground: 'linear-gradient(135deg, #556a7d 0%, #4a5f75 100%)',
      buttonHover: 'linear-gradient(135deg, #6a7f95 0%, #5f7589 100%)',

      shadowColor: 'rgba(0, 0, 0, 0.35)',
      overlayBackground: 'rgba(74, 123, 167, 0.08)',
    },
    typography: defaultTypography,
  },

  military: {
    name: 'military',
    displayName: 'Мілітарна',
    colors: {
      primary: '#6b8e4a',
      primaryLight: '#85a85f',
      primaryDark: '#527041',
      secondary: '#c9a852',

      background: '#2e3a33',
      backgroundGradientStart: '#354038',
      backgroundGradientEnd: '#2b362f',
      headerBackground: 'linear-gradient(135deg, #546e5d 0%, #4a5f52 100%)',

      textPrimary: '#e8f3f0',
      textSecondary: '#d5e5dd',
      textHeading: '#cde8d8',

      cardBackground: 'rgba(90, 130, 110, 0.18)',
      cardBorder: 'rgba(100, 150, 180, 0.3)',
      navBackground: 'rgba(90, 130, 110, 0.22)',
      navBorder: 'rgba(100, 150, 180, 0.35)',
      navActiveBackground: 'linear-gradient(135deg, #5a826a 0%, #507560 100%)',

      success: '#7db34f',
      warning: '#d9b65d',
      info: '#6ba8c9',

      linkColor: '#8fc4d4',
      linkHover: '#a8d8e8',
      buttonBackground: 'linear-gradient(135deg, #6b8e4a 0%, #5a826a 100%)',
      buttonHover: 'linear-gradient(135deg, #7ba87a 0%, #6b8e6a 100%)',

      shadowColor: 'rgba(0, 0, 0, 0.4)',
      overlayBackground: 'rgba(90, 130, 110, 0.12)',
    },
    typography: defaultTypography,
  },

  dark: {
    name: 'dark',
    displayName: 'Темна тема',
    colors: {
      primary: '#7b9fb8',
      primaryLight: '#92b3c9',
      primaryDark: '#5e8199',
      secondary: '#b89f7b',

      background: '#232830',
      backgroundGradientStart: '#2a3138',
      backgroundGradientEnd: '#252b32',
      headerBackground: 'linear-gradient(135deg, #3a4450 0%, #2f3740 100%)',

      textPrimary: '#e8ecf0',
      textSecondary: '#d0d6dc',
      textHeading: '#c9d9e6',

      cardBackground: 'rgba(123, 159, 184, 0.08)',
      cardBorder: 'rgba(123, 159, 184, 0.15)',
      navBackground: 'rgba(123, 159, 184, 0.1)',
      navBorder: 'rgba(123, 159, 184, 0.18)',
      navActiveBackground: 'linear-gradient(135deg, #526876 0%, #475662 100%)',

      success: '#66bb6a',
      warning: '#ffca28',
      info: '#7b9fb8',

      linkColor: '#92b3c9',
      linkHover: '#a9c5d7',
      buttonBackground: 'linear-gradient(135deg, #5e7a8c 0%, #526876 100%)',
      buttonHover: 'linear-gradient(135deg, #6e8a9c 0%, #627c8e 100%)',

      shadowColor: 'rgba(0, 0, 0, 0.4)',
      overlayBackground: 'rgba(123, 159, 184, 0.06)',
    },
    typography: defaultTypography,
  },

  light: {
    name: 'light',
    displayName: 'Світла тема',
    colors: {
      primary: '#4a7c9d',
      primaryLight: '#5f91b3',
      primaryDark: '#35617e',
      secondary: '#9d7c4a',

      background: '#dfe5e9',
      backgroundGradientStart: '#e5eaed',
      backgroundGradientEnd: '#dae0e4',
      headerBackground: 'linear-gradient(135deg, #c0d5e0 0%, #b3ccd9 100%)',

      textPrimary: '#1a1a1a',
      textSecondary: '#3a4550',
      textHeading: '#2a3844',

      cardBackground: 'rgba(74, 124, 157, 0.08)',
      cardBorder: 'rgba(74, 124, 157, 0.22)',
      navBackground: 'rgba(74, 124, 157, 0.1)',
      navBorder: 'rgba(74, 124, 157, 0.26)',
      navActiveBackground: 'linear-gradient(135deg, #b0cad8 0%, #a3c2d3 100%)',

      success: '#4caf50',
      warning: '#ff9800',
      info: '#5f91b3',

      linkColor: '#4a7c9d',
      linkHover: '#35617e',
      buttonBackground: 'linear-gradient(135deg, #5a86a2 0%, #4e7b94 100%)',
      buttonHover: 'linear-gradient(135deg, #6a96b2 0%, #5e8ba4 100%)',

      shadowColor: 'rgba(74, 124, 157, 0.2)',
      overlayBackground: 'rgba(74, 124, 157, 0.08)',
    },
    typography: defaultTypography,
  },

  twilight: {
    name: 'twilight',
    displayName: 'Сутінкова тема',
    colors: {
      primary: '#6a8ca8',
      primaryLight: '#7f9fb8',
      primaryDark: '#52738f',
      secondary: '#a8906a',

      background: '#5a6570',
      backgroundGradientStart: '#626d78',
      backgroundGradientEnd: '#566168',
      headerBackground: 'linear-gradient(135deg, #6d7f8e 0%, #62788b 100%)',

      textPrimary: '#f2f4f6',
      textSecondary: '#dce1e6',
      textHeading: '#e8f0f5',

      cardBackground: 'rgba(106, 140, 168, 0.15)',
      cardBorder: 'rgba(200, 220, 235, 0.3)',
      navBackground: 'rgba(106, 140, 168, 0.18)',
      navBorder: 'rgba(200, 220, 235, 0.35)',
      navActiveBackground: 'linear-gradient(135deg, #7b90a4 0%, #6f8497 100%)',

      success: '#6bc76f',
      warning: '#ffd05a',
      info: '#8fafcc',

      linkColor: '#a5c5e0',
      linkHover: '#bdd8ed',
      buttonBackground: 'linear-gradient(135deg, #7a95a9 0%, #6e8a9c 100%)',
      buttonHover: 'linear-gradient(135deg, #8aa5b9 0%, #7e9aac 100%)',

      shadowColor: 'rgba(0, 0, 0, 0.3)',
      overlayBackground: 'rgba(106, 140, 168, 0.1)',
    },
    typography: defaultTypography,
  },

  accessibility: {
    name: 'accessibility',
    displayName: 'Доступність',
    colors: {
      primary: '#000000',
      primaryLight: '#1a1a1a',
      primaryDark: '#000000',
      secondary: '#000000',

      background: '#ffffff',
      backgroundGradientStart: '#ffffff',
      backgroundGradientEnd: '#ffffff',
      headerBackground: 'linear-gradient(135deg, #000000 0%, #000000 100%)',

      textPrimary: '#000000',
      textSecondary: '#1a1a1a',
      textHeading: '#000000',

      cardBackground: '#ffffff',
      cardBorder: '#000000',
      navBackground: '#f5f5f5',
      navBorder: '#000000',
      navActiveBackground: 'linear-gradient(135deg, #000000 0%, #000000 100%)',

      success: '#000000',
      warning: '#000000',
      info: '#000000',

      linkColor: '#000000',
      linkHover: '#333333',
      buttonBackground: 'linear-gradient(135deg, #000000 0%, #000000 100%)',
      buttonHover: 'linear-gradient(135deg, #1a1a1a 0%, #1a1a1a 100%)',

      shadowColor: 'rgba(0, 0, 0, 0.5)',
      overlayBackground: 'rgba(0, 0, 0, 0.1)',
    },
    typography: {
      ...defaultTypography,
      fontSizeBase: '1.25rem',
      fontSizeSmall: '1.1rem',
      fontSizeLarge: '1.5rem',
      fontSizeH1: '3rem',
      fontSizeH2: '2.5rem',
      fontSizeH3: '2rem',
      fontWeightBold: '800',
      lineHeightBase: '1.8',
    },
  },

  toxic: {
    name: 'toxic',
    displayName: 'Токсична',
    colors: {
      primary: '#00ff41',
      primaryLight: '#39ff6d',
      primaryDark: '#00cc34',
      secondary: '#ff00ff',

      background: '#0a0014',
      backgroundGradientStart: '#1a0028',
      backgroundGradientEnd: '#0f001f',
      headerBackground: 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)',

      textPrimary: '#00ff41',
      textSecondary: '#39ff6d',
      textHeading: '#ff00ff',

      cardBackground: 'rgba(0, 255, 65, 0.15)',
      cardBorder: 'rgba(255, 0, 255, 0.5)',
      navBackground: 'rgba(0, 255, 255, 0.12)',
      navBorder: 'rgba(255, 0, 255, 0.6)',
      navActiveBackground: 'linear-gradient(135deg, #ff00ff 0%, #00ff41 100%)',

      success: '#00ff41',
      warning: '#ffff00',
      info: '#00ffff',

      linkColor: '#00ffff',
      linkHover: '#ff00ff',
      buttonBackground: 'linear-gradient(135deg, #ff00ff 0%, #00ff41 100%)',
      buttonHover: 'linear-gradient(135deg, #00ffff 0%, #ff00ff 100%)',

      shadowColor: 'rgba(255, 0, 255, 0.5)',
      overlayBackground: 'rgba(0, 255, 65, 0.15)',
    },
    typography: defaultTypography,
  },

  custom: {
    name: 'custom',
    displayName: 'Власна тема',
    colors: {
      primary: '#4da6ff',
      primaryLight: '#66b3ff',
      primaryDark: '#2e86de',
      secondary: '#ff6b9d',

      background: '#1e2433',
      backgroundGradientStart: '#2a3142',
      backgroundGradientEnd: '#1e3a5f',
      headerBackground: 'linear-gradient(135deg, #2a3142 0%, #1e2d47 100%)',

      textPrimary: '#ffffff',
      textSecondary: '#f5f5f5',
      textHeading: '#66b3ff',

      cardBackground: 'rgba(77, 166, 255, 0.15)',
      cardBorder: 'rgba(77, 166, 255, 0.3)',
      navBackground: 'rgba(77, 166, 255, 0.2)',
      navBorder: 'rgba(77, 166, 255, 0.4)',
      navActiveBackground: 'linear-gradient(135deg, #4da6ff 0%, #2e86de 100%)',

      success: '#66bb6a',
      warning: '#ffca28',
      info: '#66b3ff',

      linkColor: '#66b3ff',
      linkHover: '#99d6ff',
      buttonBackground: 'linear-gradient(135deg, #4da6ff 0%, #2e86de 100%)',
      buttonHover: 'linear-gradient(135deg, #66b3ff 0%, #4da6ff 100%)',

      shadowColor: 'rgba(0, 0, 0, 0.3)',
      overlayBackground: 'rgba(77, 166, 255, 0.1)',
    },
    typography: { ...defaultTypography },
  },
};

export const defaultTheme = themes.ukrainian;
